import { ref, computed } from "vue";
import type { ISatMapProps } from "@chatfood/design-system";
import type { IDomainOutletPickup } from "~/domain/outlet-pickup";
import { useBusinessStore } from "~/stores/business";
import { useNavigation } from "~/composables/use-navigation";

export const usePickup = () => {
  const business = useBusinessStore();
  const { push } = useNavigation();

  const isLoading = ref(false);
  const region = ref(business.country);
  const locationsSource = ref<IDomainOutletPickup[]>([]);
  const clickedLocation = ref<IDomainOutletPickup>();
  const focusedLocation = ref<IDomainOutletPickup>();

  const mapBoundaries = ref<ISatMapProps["mapBounds"]>({
    location: business.country,
  });

  const locations = computed(() => {
    return locationsSource.value.map((location) => ({
      ...location,
      isSelected: clickedLocation.value?.outletId === location.outletId,
      isFocused: focusedLocation.value?.outletId === location.outletId,
    }));
  });

  const onLocationsLoaded = (data: IDomainOutletPickup[]) => {
    locationsSource.value = data;
  };

  const onRegionChanged = (value: string) => {
    region.value = value || business.country;
    clickedLocation.value = undefined;
    focusedLocation.value = undefined;
    mapBoundaries.value = {
      location: region.value,
    };
  };

  const onLocationClicked = (location: IDomainOutletPickup) => {
    clickedLocation.value = location;

    if (clickedLocation.value) {
      mapBoundaries.value = {
        coordinates: {
          lat: clickedLocation.value.coordinates.lat,
          lng: clickedLocation.value.coordinates.lng,
        },
      };
    }
  };

  const onLocationFocused = (location: IDomainOutletPickup) => {
    focusedLocation.value = location;
  };

  const onLocationBlur = () => {
    focusedLocation.value = undefined;
  };

  const onPickupAt = async (outletSlug: string) => {
    isLoading.value = true;

    try {
      await push("outlet", {
        orderingMode: "pickup",
        outletSlug,
      });
    } catch {
      isLoading.value = false;
    }
  };

  return {
    mapBoundaries,
    isLoading,
    clickedLocation,
    focusedLocation,
    locations,
    onLocationsLoaded,
    onRegionChanged,
    onLocationClicked,
    onLocationBlur,
    onLocationFocused,
    onPickupAt,
  };
};
